import { applyPatches, enableMapSet, enablePatches, produce, produceWithPatches } from 'immer';
import debounce from 'lodash/debounce.js';
import isEqual from 'lodash/isEqual.js';
import { createStore } from 'zustand';
import { devtools as devtoolsMiddleware } from 'zustand/middleware';
import { generateDiscriminatedDefault } from "../utils/defaults.js";
import { getPath } from "../utils/path.js";
import { createSchemaContext, resolveSchemaAtPath } from "../utils/schema-context.js";
import { composeValidators, createSchemaValidator } from "../validation/schema-validator.js";
let immerConfigured = false;
function ensureImmerConfig() {
    if (!immerConfigured) {
        enablePatches();
        enableMapSet();
        immerConfigured = true;
    }
}
/**
 * Immer-safe alternative to lodash.set. Traverses the draft manually,
 * creating intermediate objects or arrays as needed based on path segments.
 * Supports dot notation and bracket notation (e.g., "a.b[0].c").
 */
function setNestedValue(draft, path, value) {
    const segments = path.split(/[.[\]]+/).filter(Boolean);
    let current = draft;
    for (let i = 0; i < segments.length - 1; i++) {
        const seg = segments[i];
        const nextSeg = segments[i + 1];
        if (current[seg] === undefined || current[seg] === null) {
            current[seg] = /^\d+$/.test(nextSeg) ? [] : {};
        }
        current = current[seg];
    }
    current[segments[segments.length - 1]] = value;
}
function generateKey() {
    return Math.random().toString(36).slice(2, 11);
}
function ensureArrayKeys(arrayKeys, path, arrayLength) {
    const existingKeys = arrayKeys[path] ?? [];
    if (existingKeys.length === arrayLength)
        return existingKeys;
    // Truncate if array shrank, extend if array grew
    const newKeys = existingKeys.slice(0, arrayLength);
    while (newKeys.length < arrayLength) {
        newKeys.push(generateKey());
    }
    return newKeys;
}
export function createFormStore(options) {
    ensureImmerConfig();
    const { name, schema, defaultValues, config, schemaValidator, validator: customValidator, onSubmit, autoSubmit = false, autoSubmitDelay = 300, validateOnBlur = true, enablePatches: patchesEnabled = false, parent } = options;
    // Create schema context for resolving $ref
    const schemaCtx = createSchemaContext(schema);
    // Build the combined validator:
    // - schemaValidator defaults to ajv schema validation
    // - customValidator (if provided) is composed with schemaValidator
    const baseValidator = schemaValidator ?? createSchemaValidator(schema);
    const validator = customValidator ? composeValidators(baseValidator, customValidator) : baseValidator;
    const storeCreator = (set, get) => {
        // Debounced auto-submit calls submit() to ensure validation runs before onSubmit
        const debouncedAutoSubmit = autoSubmit
            ? debounce(() => {
                void get().submit();
            }, autoSubmitDelay)
            : null;
        const triggerAutoSubmit = () => {
            if (debouncedAutoSubmit)
                debouncedAutoSubmit();
        };
        // Debounced validation on change (500ms, matching schema-form behavior).
        // Uses takeLatest semantics — only the last change triggers validation.
        // Sets isValidating immediately so consumers know a result is pending.
        const debouncedValidation = debounce(() => {
            void runValidation();
        }, 500);
        const scheduleValidation = () => {
            set({ isValidating: true });
            debouncedValidation();
        };
        const updateState = (updater) => {
            if (patchesEnabled) {
                const [nextState, patches, inversePatches] = produceWithPatches(get(), (draft) => {
                    updater(draft);
                    draft.isDirty = draft.dirty.size > 0;
                });
                set({
                    ...nextState,
                    patches: [...get().patches, ...patches],
                    inversePatches: [...get().inversePatches, ...inversePatches]
                });
            }
            else {
                set(produce(get(), (draft) => {
                    updater(draft);
                    draft.isDirty = draft.dirty.size > 0;
                }));
            }
        };
        const runValidation = async () => {
            set({ isValidating: true });
            try {
                const result = validator(get().values);
                const errors = result instanceof Promise ? await result : result;
                const isValid = Object.keys(errors).length === 0;
                set({ errors, isValid, isValidating: false });
                return isValid;
            }
            catch (error) {
                console.error('Validation error:', error);
                set({ isValidating: false });
                return false;
            }
        };
        return {
            name,
            schema,
            values: structuredClone(defaultValues),
            defaultValues: structuredClone(defaultValues),
            config,
            errors: {},
            isValid: true,
            isValidating: false,
            isSubmitting: false,
            submitCount: 0,
            lastSubmittedAt: undefined,
            touched: new Set(),
            dirty: new Set(),
            isDirty: false,
            pendingEdits: new Set(),
            arrayKeys: {},
            collapsed: new Set(),
            autoSubmit,
            autoSubmitDelay,
            validateOnBlur,
            showAllErrors: false,
            enablePatches: patchesEnabled,
            patches: [],
            inversePatches: [],
            parent,
            setFieldValue: (path, value) => {
                updateState((s) => {
                    setNestedValue(s.values, path, value);
                    const existingValue = getPath(s.defaultValues, path);
                    if (isEqual(value, existingValue)) {
                        s.dirty.delete(path);
                    }
                    else {
                        s.dirty.add(path);
                    }
                });
                scheduleValidation();
                triggerAutoSubmit();
            },
            setFieldTouched: (path, touched = true) => updateState((s) => {
                if (touched)
                    s.touched.add(path);
                else
                    s.touched.delete(path);
            }),
            setFieldError: (path, error) => set(produce(get(), (d) => {
                if (error) {
                    d.errors[path] = error;
                    d.isValid = false;
                }
                else {
                    delete d.errors[path];
                    d.isValid = Object.keys(d.errors).length === 0;
                }
            })),
            clearFieldError: (path) => set(produce(get(), (d) => {
                delete d.errors[path];
                d.isValid = Object.keys(d.errors).length === 0;
            })),
            beginEdit: (path) => updateState((s) => {
                s.pendingEdits.add(path);
            }),
            commitEdit: (path, value) => {
                updateState((s) => {
                    s.pendingEdits.delete(path);
                    if (getPath(s.values, path) !== value) {
                        setNestedValue(s.values, path, value);
                        s.dirty.add(path);
                    }
                });
                triggerAutoSubmit();
            },
            cancelEdit: (path) => updateState((s) => {
                s.pendingEdits.delete(path);
            }),
            setDiscriminatorValue: (path, discriminatorPath, value) => {
                updateState((s) => {
                    const fieldSchema = resolveSchemaAtPath(schemaCtx, path);
                    if (!fieldSchema)
                        return;
                    const newValues = generateDiscriminatedDefault(fieldSchema, discriminatorPath, value, schemaCtx);
                    if (!isEqual(getPath(s.values, path), newValues)) {
                        setNestedValue(s.values, path, newValues);
                        s.dirty.add(path);
                    }
                });
                triggerAutoSubmit();
            },
            arrayAppend: (path, value) => {
                updateState((s) => {
                    const array = getPath(s.values, path) ?? [];
                    const keys = ensureArrayKeys(s.arrayKeys, path, array.length);
                    array.push(value);
                    keys.push(generateKey());
                    setNestedValue(s.values, path, array);
                    s.dirty.add(path);
                    s.arrayKeys[path] = keys;
                });
                triggerAutoSubmit();
            },
            arrayInsert: (path, index, value) => {
                updateState((s) => {
                    const array = getPath(s.values, path) ?? [];
                    const keys = ensureArrayKeys(s.arrayKeys, path, array.length);
                    array.splice(index, 0, value);
                    keys.splice(index, 0, generateKey());
                    setNestedValue(s.values, path, array);
                    s.dirty.add(path);
                    s.arrayKeys[path] = keys;
                });
                triggerAutoSubmit();
            },
            arrayRemove: (path, index) => {
                updateState((s) => {
                    const array = getPath(s.values, path) ?? [];
                    const keys = ensureArrayKeys(s.arrayKeys, path, array.length);
                    array.splice(index, 1);
                    keys.splice(index, 1);
                    setNestedValue(s.values, path, array);
                    s.dirty.add(path);
                    s.arrayKeys[path] = keys;
                });
                triggerAutoSubmit();
            },
            arrayMove: (path, from, to) => {
                updateState((s) => {
                    const array = getPath(s.values, path) ?? [];
                    const keys = ensureArrayKeys(s.arrayKeys, path, array.length);
                    const [item] = array.splice(from, 1);
                    array.splice(to, 0, item);
                    const [key] = keys.splice(from, 1);
                    keys.splice(to, 0, key);
                    setNestedValue(s.values, path, array);
                    s.dirty.add(path);
                    s.arrayKeys[path] = keys;
                });
                triggerAutoSubmit();
            },
            arraySwap: (path, indexA, indexB) => {
                updateState((s) => {
                    const array = getPath(s.values, path) ?? [];
                    const keys = ensureArrayKeys(s.arrayKeys, path, array.length);
                    [array[indexA], array[indexB]] = [array[indexB], array[indexA]];
                    [keys[indexA], keys[indexB]] = [keys[indexB], keys[indexA]];
                    setNestedValue(s.values, path, array);
                    s.dirty.add(path);
                    s.arrayKeys[path] = keys;
                });
                triggerAutoSubmit();
            },
            recordSet: (path, key, value) => {
                updateState((s) => {
                    const record = { ...(getPath(s.values, path) ?? {}) };
                    record[key] = value;
                    setNestedValue(s.values, path, record);
                    s.dirty.add(path);
                });
                triggerAutoSubmit();
            },
            recordRemove: (path, key) => {
                updateState((s) => {
                    const record = { ...(getPath(s.values, path) ?? {}) };
                    delete record[key];
                    setNestedValue(s.values, path, record);
                    s.dirty.add(path);
                });
                triggerAutoSubmit();
            },
            toggleCollapsed: (path) => set(produce(get(), (d) => {
                if (d.collapsed.has(path))
                    d.collapsed.delete(path);
                else
                    d.collapsed.add(path);
            })),
            setCollapsed: (path, collapsed) => set(produce(get(), (d) => {
                if (collapsed)
                    d.collapsed.add(path);
                else
                    d.collapsed.delete(path);
            })),
            validate: async () => {
                set({ showAllErrors: true });
                return runValidation();
            },
            validateSilent: runValidation,
            validateField: async (path) => {
                try {
                    const result = validator(get().values);
                    const allErrors = result instanceof Promise ? await result : result;
                    const fieldError = allErrors[path];
                    // Use functional set to safely merge with current state after async gap
                    set((state) => produce(state, (d) => {
                        if (fieldError) {
                            d.errors[path] = fieldError;
                        }
                        else {
                            delete d.errors[path];
                        }
                        d.isValid = Object.keys(d.errors).length === 0;
                    }));
                    return fieldError;
                }
                catch (error) {
                    console.error('Field validation error:', error);
                    return undefined;
                }
            },
            reset: (values) => {
                debouncedAutoSubmit?.cancel();
                debouncedValidation.cancel();
                const newValues = values
                    ? { ...structuredClone(get().defaultValues), ...values }
                    : structuredClone(get().defaultValues);
                set({
                    values: newValues,
                    errors: {},
                    isValid: true,
                    isValidating: false,
                    touched: new Set(),
                    dirty: new Set(),
                    isDirty: false,
                    pendingEdits: new Set(),
                    collapsed: new Set(),
                    showAllErrors: false,
                    patches: [],
                    inversePatches: []
                });
            },
            setValues: (values) => {
                updateState((s) => {
                    s.values = structuredClone(values);
                    if (typeof values === 'object' && values !== null)
                        for (const key of Object.keys(values))
                            s.dirty.add(key);
                });
                triggerAutoSubmit();
            },
            submit: async () => {
                const state = get();
                if (state.isSubmitting)
                    return;
                set({ isSubmitting: true, showAllErrors: true });
                try {
                    const isValid = await runValidation();
                    if (!isValid) {
                        set({ isSubmitting: false });
                        return;
                    }
                    // Capture values immediately after validation to avoid race
                    const validatedValues = get().values;
                    if (onSubmit)
                        await onSubmit(validatedValues);
                    set((s) => ({
                        ...s,
                        isSubmitting: false,
                        submitCount: s.submitCount + 1,
                        lastSubmittedAt: Date.now(),
                        dirty: new Set(),
                        isDirty: false
                    }));
                }
                catch (error) {
                    console.error('Submit error:', error);
                    set({ isSubmitting: false });
                    throw error;
                }
            },
            setSubmitting: (isSubmitting) => set({ isSubmitting }),
            sync: async () => {
                const { parent: p, values } = get();
                if (!p)
                    return false;
                const isValid = await runValidation();
                if (!isValid)
                    return false;
                // Call custom onSync if provided
                if (p.onSync) {
                    const handled = p.onSync(values, p.store);
                    if (handled) {
                        set({ dirty: new Set(), isDirty: false });
                        return true;
                    }
                }
                // Default sync behavior
                if (p.include && p.include.length > 0 && p.path === '') {
                    // For root-level property subsets, spread values into parent
                    const parentState = p.store.getState();
                    const parentValues = parentState.values;
                    parentState.setValues({ ...parentValues, ...values });
                }
                else {
                    // Standard nested path sync
                    p.store.getState().setFieldValue(p.path, structuredClone(values));
                }
                set({ dirty: new Set(), isDirty: false });
                return true;
            },
            discard: () => {
                const { parent: p, defaultValues: dv } = get();
                if (!p)
                    return;
                set({
                    values: structuredClone(dv),
                    errors: {},
                    isValid: true,
                    dirty: new Set(),
                    isDirty: false,
                    touched: new Set(),
                    pendingEdits: new Set()
                });
            },
            refresh: () => {
                const { parent: p } = get();
                if (!p)
                    return;
                let freshValues;
                if (p.include && p.include.length > 0 && p.path === '') {
                    // For root-level property subsets, extract only included properties
                    const parentValues = p.store.getState().values;
                    const result = {};
                    for (const key of p.include) {
                        if (key in parentValues) {
                            result[key] = parentValues[key];
                        }
                    }
                    freshValues = result;
                }
                else {
                    freshValues = getPath(p.store.getState().values, p.path);
                }
                if (freshValues !== undefined)
                    set({
                        values: structuredClone(freshValues),
                        defaultValues: structuredClone(freshValues),
                        errors: {},
                        isValid: true,
                        dirty: new Set(),
                        isDirty: false,
                        touched: new Set(),
                        pendingEdits: new Set()
                    });
            },
            getPatches: () => (patchesEnabled ? get().patches : []),
            clearPatches: () => {
                if (patchesEnabled)
                    set({ patches: [], inversePatches: [] });
            },
            applyInversePatches: () => {
                if (!patchesEnabled)
                    return;
                const { inversePatches, values } = get();
                if (inversePatches.length === 0)
                    return;
                const newValues = applyPatches(structuredClone(values), inversePatches);
                set({ values: newValues, patches: [], inversePatches: [], dirty: new Set(), isDirty: false });
            },
            destroy: () => {
                debouncedAutoSubmit?.cancel();
                debouncedValidation.cancel();
            }
        };
    };
    if (options.devtools) {
        const devtoolsName = typeof options.devtools === 'string' ? options.devtools : options.name;
        return createStore()(devtoolsMiddleware(storeCreator, { name: devtoolsName }));
    }
    return createStore(storeCreator);
}
